import { useCallback, useMemo } from 'react'
import { useInfiniteQuery } from 'react-query'

import { useNotificationMutations } from 'features/notifications/hooks/use-list-notifications/use-notification-mutations'
import type { NotificationsService } from 'features/notifications/service'
import { isForbiddenError } from 'features/notifications/service/service'
import type { NotificationDto } from 'features/notifications/service/types'

/**
 * Default polling cadence (ms). Hand-rolled retries / debouncing would
 * compete with react-query — keep it inside the QueryClient by leaning on
 * `refetchInterval` + `staleTime` per `.agents/rules/tool-mastery.md`.
 */
const POLL_INTERVAL_MS = 60_000

/**
 * `staleTime` for the list query. Pairs with `refetchOnWindowFocus` to keep
 * the bell badge fresh without thrashing the API on every render.
 */
const LIST_STALE_TIME_MS = 30_000

/**
 * Page size for the panel's "Load more" pagination — mirrors the
 * backend's default `limit`. A short page can still be followed by more
 * rows (the backend drops rows whose polymorphic context resolved to
 * null post-LIMIT), so "has more" is keyed on non-empty, not full.
 */
const PAGE_SIZE = 50

const notificationsQueryKey = (onlyUnread: boolean) =>
  ['notifications', 'list', { only_unread: onlyUnread }] as const

const countUnread = (notifications: ReadonlyArray<NotificationDto>): number =>
  notifications.filter((n) => n.acknowledged_at === null).length

export interface UseNotificationsResult {
  notifications: NotificationDto[]
  /**
   * Unread rows among the LOADED pages — the badge count. With the list
   * now paged, an unread backlog deeper than the loaded pages doesn't
   * inflate the badge; acceptable for a bell surface.
   */
  unreadCount: number
  isLoading: boolean
  isError: boolean
  /** Caller is outside the surface's audience — hide the bell. */
  isForbidden: boolean
  hasMore: boolean
  isLoadingMore: boolean
  loadMore: () => void
  refetch: () => Promise<unknown>
  markManyAsRead: (ids: number[]) => Promise<void>
  markManyAsUnread: (ids: number[]) => Promise<void>
  deleteMany: (ids: number[]) => Promise<void>
  isMutating: boolean
}

export function useListNotifications(
  service: NotificationsService,
  onlyUnread: boolean,
): UseNotificationsResult {
  const {
    data,
    error,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    notificationsQueryKey(onlyUnread),
    ({ pageParam = 0 }) =>
      service.fetchMyNotifications(onlyUnread, PAGE_SIZE, Number(pageParam)),
    {
      staleTime: LIST_STALE_TIME_MS,
      // A forbidden caller stays forbidden — stop the poll on a settled
      // 403 instead of re-asking every minute. (Initial retries follow
      // the QueryClient default; only the standing poll is cut.)
      refetchInterval: (_, query) =>
        isForbiddenError(query.state.error) ? false : POLL_INTERVAL_MS,
      refetchOnWindowFocus: true,
      // Empty page = the backlog is exhausted. Non-empty (even short —
      // see PAGE_SIZE doc) may still have more; the next offset advances
      // by the page COUNT so context-filtered rows aren't skipped over.
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === 0 ? undefined : allPages.length * PAGE_SIZE,
    },
  )

  // `pages` entries are typed NotificationDto[] but the boundary is an
  // HTTP response, so guard against malformed payloads rather than crash
  // the whole component tree on `.filter is not a function`.
  const notifications = useMemo(
    () =>
      (data?.pages ?? []).flatMap((page) => (Array.isArray(page) ? page : [])),
    [data],
  )
  const unreadCount = useMemo(() => countUnread(notifications), [notifications])

  const mutations = useNotificationMutations(service)

  const loadMore = useCallback((): void => {
    void fetchNextPage()
  }, [fetchNextPage])

  return {
    notifications,
    unreadCount,
    isLoading,
    isError,
    isForbidden: isForbiddenError(error),
    hasMore: hasNextPage === true,
    isLoadingMore: isFetchingNextPage,
    loadMore,
    refetch,
    ...mutations,
  }
}
